import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useCmsPage, c } from '../../lib/cms';

const jsonLd = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://xentrios.com' },
        { '@type': 'ListItem', position: 2, name: 'Compare', item: 'https://xentrios.com/compare' },
        {
          '@type': 'ListItem',
          position: 3,
          name: 'Xentrios vs Dentrix Enterprise, Denticon & CareStack',
          item: 'https://xentrios.com/compare/xentrios-vs-legacy-pms',
        },
      ],
    },
    {
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: 'Is migrating from Dentrix Enterprise or Denticon to Xentrios realistic for a large DSO?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'PMS migration at scale is a significant operational undertaking regardless of the destination platform. For a DSO considering Xentrios, the migration path typically involves: a parallel-run period where Xentrios operates alongside the legacy PMS, NDS DataVault migrating historical data and maintaining continuity for analytics and reporting, and a phased location rollout rather than a simultaneous cutover. The complexity is real — legacy PMS platforms have deep roots in clinical workflows and billing processes. The right question is not whether migration is possible, but whether the long-term architecture advantage of a cloud-native DSO-integrated platform justifies the transition investment for your group.',
          },
        },
        {
          '@type': 'Question',
          name: 'What happens to historical data if we move to Xentrios?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'NDS DataVault handles historical data migration and continuity. Patient records, treatment history, claim history, and financial records from your legacy PMS are extracted, normalized, and loaded into DataVault before go-live. Xentrios is provisioned with the full historical dataset, so your clinical and billing teams maintain access to prior-year records without relying on the legacy system. DataVault also retains the historical data in the warehouse for long-term analytics continuity — denial rate trending, payer benchmarking, and AR aging comparisons remain available across the migration boundary.',
          },
        },
        {
          '@type': 'Question',
          name: 'How does CareStack compare to Xentrios? Both are cloud-native.',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'CareStack is a capable cloud dental PMS with strong multi-location features and a modern architecture. The primary difference from Xentrios is integration depth: Xentrios is purpose-built to run natively with NDS DataVault and the RevTech RCM suite — InsightFlow, DenialIQ, ClaimVision, PayerIQ, and ReconIQ are first-party integrations, not third-party connections. For DSOs that want the analytics and RCM intelligence layer pre-integrated into their PMS rather than connected via a separate data pipeline, Xentrios offers a tighter architecture. CareStack is a strong standalone PMS choice and RevTech does support CareStack via NDS DataVault connectors.',
          },
        },
        {
          '@type': 'Question',
          name: 'Which situations favor staying with a legacy PMS like Dentrix Enterprise or Denticon?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'There are legitimate reasons to stay with legacy platforms. Dentrix Enterprise and Denticon have large install bases, extensive training material, and support ecosystems built around them. If your clinical staff is deeply trained on a legacy platform, your billing workflows are optimized around it, and your current denial rates and RCM performance are meeting benchmarks, the disruption of a PMS migration may not be justified. Legacy platforms also offer mature integration ecosystems for specialty software (imaging, perio charting, patient communication) that newer platforms are still building. The RevTech + NDS DataVault stack can layer on top of legacy PMS platforms, so groups don\'t have to choose between modern RCM analytics and their existing PMS.',
          },
        },
      ],
    },
  ],
};

const tableRows = [
  {
    criterion: 'Cloud-native architecture',
    xentrios: { value: 'Fully cloud-native — no server hardware, automatic updates, accessible from any location', check: true },
    dentrix: { value: 'Server-based architecture; cloud-hosted options exist but not cloud-native by design', check: false },
    denticon: { value: 'Cloud-based (hosted model) but architecture predates modern cloud-native design patterns', check: false },
    carestack: { value: 'Cloud-native — one of the few established cloud-first dental PMS platforms', check: true },
  },
  {
    criterion: 'Multi-location built-in',
    xentrios: { value: 'Designed from the ground up for multi-location DSO operations — enterprise role hierarchy native', check: true },
    dentrix: { value: 'Enterprise edition supports multi-location; requires significant configuration', check: false },
    denticon: { value: 'Multi-location support is a core feature — historically strong for DSOs', check: true },
    carestack: { value: 'Strong multi-location support — built for groups from the start', check: true },
  },
  {
    criterion: 'RCM analytics integration',
    xentrios: { value: 'Native integration with InsightFlow, DenialIQ, ClaimVision, PayerIQ, and ReconIQ — no pipeline configuration', check: true },
    dentrix: { value: 'Via NDS DataVault connector — capable but requires separate integration layer', check: false },
    denticon: { value: 'Via NDS DataVault connector — RevTech analytics accessible but not natively integrated', check: false },
    carestack: { value: 'Via NDS DataVault connector — RevTech analytics accessible but not natively integrated', check: false },
  },
  {
    criterion: 'Data warehouse integration',
    xentrios: { value: 'NDS DataVault is the native data layer — real-time sync, full claim and payment data extraction', check: true },
    dentrix: { value: 'NDS DataVault supported; batch extraction; requires connector configuration', check: false },
    denticon: { value: 'NDS DataVault supported; batch extraction; requires connector configuration', check: false },
    carestack: { value: 'NDS DataVault supported; near-real-time sync available', check: false },
  },
  {
    criterion: 'AI workflow support',
    xentrios: { value: 'AI features across scheduling, clinical documentation, denial prediction, and claim risk scoring', check: true },
    dentrix: { value: 'Limited AI features — primarily a workflow management platform with traditional design', check: false },
    denticon: { value: 'Limited AI features; modernization roadmap in progress', check: false },
    carestack: { value: 'AI features available; actively developing AI-assisted workflows', check: true },
  },
  {
    criterion: 'Migration complexity',
    xentrios: { value: 'Complex — phased rollout with NDS DataVault historical data migration; significant change management', check: false },
    dentrix: { value: 'Lower from other Dentrix versions; complex from non-Henry Schein platforms', check: false },
    denticon: { value: 'Moderate for inbound; complex for outbound migration', check: false },
    carestack: { value: 'Moderate complexity with dedicated migration support team', check: false },
  },
  {
    criterion: 'API openness',
    xentrios: { value: 'Open API architecture — third-party integrations supported natively', check: true },
    dentrix: { value: 'API available but historically more restricted; Henry Schein Marketplace for vetted integrations', check: false },
    denticon: { value: 'API available; Planet DDS partner ecosystem for integrations', check: true },
    carestack: { value: 'Open API with active developer partner program', check: true },
  },
  {
    criterion: 'Pricing model',
    xentrios: { value: 'DSO-oriented enterprise pricing; bundled with NDS + RevTech stack pricing available', check: true },
    dentrix: { value: 'Per-provider or per-workstation licensing; enterprise agreements for large groups', check: true },
    denticon: { value: 'Per-location subscription; enterprise agreements for large DSOs', check: true },
    carestack: { value: 'Per-location SaaS subscription; enterprise pricing available', check: true },
  },
  {
    criterion: 'Support model',
    xentrios: { value: 'Dedicated DSO success team; NDS managed IT services available for infrastructure support', check: true },
    dentrix: { value: 'Large established support network; Henry Schein field support available', check: true },
    denticon: { value: 'Established DSO-focused support team; Planet DDS customer success', check: true },
    carestack: { value: 'US-based support team with DSO-specific customer success resources', check: true },
  },
  {
    criterion: 'DSO-specific design',
    xentrios: { value: 'Built exclusively for DSO architecture — enterprise AR roll-up, multi-location reporting, and centralized billing native', check: true },
    dentrix: { value: 'Originally designed for single practices; Enterprise edition adds DSO-specific features', check: false },
    denticon: { value: 'Strong DSO design history — multi-location and centralized billing are core features', check: true },
    carestack: { value: 'DSO-focused design; enterprise features continue to mature', check: true },
  },
];

export default function XentriosVsLegacyPMSPage() {
  const cmsPage = useCmsPage('xentrios', 'xentrios-vs-legacy-pms');
  const heroHeadline = c(cmsPage, 'hero', 'headline', 'Xentrios vs. Dentrix Enterprise, Denticon & CareStack');
  const heroSubtitle = c(cmsPage, 'hero', 'subtitle', 'Dentrix Enterprise and Denticon are proven, widely-deployed PMS platforms with large install bases and established support ecosystems. CareStack is a newer cloud platform gaining traction with DSOs. Xentrios takes a different architectural approach: built natively for DSOs with the full NDS + RevTech analytics stack integrated from the start. Here is an honest evaluation of all four.');
  const tableRowItems = c(cmsPage, 'features', 'items', tableRows);
  return (
    <div>
      <Helmet>
        <title>Xentrios vs Dentrix Enterprise, Denticon & CareStack — Cloud Dental PMS for DSOs</title>
        <meta
          name="description"
          content="Compare Xentrios vs. Dentrix Enterprise, Denticon, and CareStack for multi-location DSOs. Cloud-native vs. server-based PMS, native analytics integration, AI support, and DSO-specific multi-location design."
        />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      {/* Hero */}
      <section className="xen-hero">
        <div className="xen-container">
          <p className="xen-hero-eyebrow">Platform Comparison</p>
          <h1>{heroHeadline}</h1>
          <p className="subtitle">{heroSubtitle}</p>
          <div className="xen-hero-actions">
            <Link to="/demo" className="xen-btn xen-btn-primary" style={{ padding: '0.75rem 2rem', fontSize: '1rem' }}>
              See Xentrios in Action
            </Link>
            <Link
              to="/stack"
              className="xen-btn xen-btn-outline"
              style={{ padding: '0.75rem 2rem', fontSize: '1rem', borderColor: '#fff', color: '#fff' }}
            >
              Explore the Full Stack
            </Link>
          </div>
        </div>
      </section>

      {/* Intro framing */}
      <section className="xen-section xen-section-light">
        <div className="xen-container">
          <p className="xen-section-eyebrow">Market Context</p>
          <h2>A fair look at the dental PMS landscape for DSOs.</h2>
          <p className="xen-section-subtitle" style={{ marginBottom: '2rem' }}>
            The dental PMS market includes platforms with decades of clinical workflow history alongside
            newer entrants built for a cloud-first, analytics-integrated future. Each has genuine strengths.
            The right choice depends on your group's current infrastructure, growth trajectory, and analytics priorities.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1.5rem', marginTop: '1.5rem' }}>
            <div style={{ background: '#f8f9fc', borderRadius: '12px', padding: '1.75rem' }}>
              <h3 style={{ color: 'var(--xen-navy)', marginBottom: '0.75rem', fontSize: '1rem' }}>Dentrix Enterprise</h3>
              <p style={{ fontSize: '0.9rem', color: '#555', lineHeight: 1.7 }}>
                The largest installed base of any dental PMS in the U.S. Originally built for single
                practices; Enterprise edition adds multi-location capabilities for DSOs. Deep integration
                with the Henry Schein ecosystem of products and supplies. Server-based architecture means
                each location typically has on-site infrastructure. A proven, stable platform for groups
                that prioritize continuity and a large support network.
              </p>
            </div>
            <div style={{ background: '#f8f9fc', borderRadius: '12px', padding: '1.75rem' }}>
              <h3 style={{ color: 'var(--xen-navy)', marginBottom: '0.75rem', fontSize: '1rem' }}>Denticon</h3>
              <p style={{ fontSize: '0.9rem', color: '#555', lineHeight: 1.7 }}>
                Planet DDS's hosted multi-location PMS with a long history in the DSO segment. Denticon
                was purpose-designed for multi-location groups — centralized billing, role-based reporting,
                and enterprise scheduling are foundational, not add-ons. A hosted (not cloud-native) model
                means Planet DDS manages the servers. Strong choice for mid-size DSOs with established
                Denticon workflows.
              </p>
            </div>
            <div style={{ background: '#f8f9fc', borderRadius: '12px', padding: '1.75rem' }}>
              <h3 style={{ color: 'var(--xen-navy)', marginBottom: '0.75rem', fontSize: '1rem' }}>CareStack</h3>
              <p style={{ fontSize: '0.9rem', color: '#555', lineHeight: 1.7 }}>
                A cloud-native PMS that has grown quickly in the DSO segment. CareStack offers a modern
                architecture with built-in multi-location features, an open API, and an active partner
                ecosystem. Actively developing AI-assisted workflows. RevTech analytics are available
                via NDS DataVault connectors. A strong choice for groups that want cloud-native without
                making the full Xentrios stack commitment.
              </p>
            </div>
            <div style={{ background: '#f0f4ff', borderRadius: '12px', padding: '1.75rem', border: '1.5px solid var(--xen-accent)' }}>
              <h3 style={{ color: 'var(--xen-navy)', marginBottom: '0.75rem', fontSize: '1rem' }}>Xentrios</h3>
              <p style={{ fontSize: '0.9rem', color: '#555', lineHeight: 1.7 }}>
                Built specifically for DSOs from the ground up with the NDS + RevTech stack integrated
                natively. InsightFlow, DenialIQ, ClaimVision, PayerIQ, and ReconIQ are not third-party
                connections — they are part of the same product architecture. AI features are embedded
                across scheduling, documentation, and claim workflows. NDS DataVault is the underlying
                data layer, providing real-time sync and a full data warehouse alongside the PMS.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison table */}
      <section className="xen-section" style={{ background: '#fff' }}>
        <div className="xen-container">
          <p className="xen-section-eyebrow">Side-by-Side Comparison</p>
          <h2>Xentrios vs. Dentrix Enterprise vs. Denticon vs. CareStack</h2>
          <p className="xen-section-subtitle" style={{ marginBottom: '2rem' }}>
            Evaluated across 10 criteria relevant to DSO PMS platform decisions.
          </p>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem', minWidth: '800px' }}>
              <thead>
                <tr style={{ background: 'var(--xen-navy)', color: '#fff' }}>
                  <th style={{ padding: '0.85rem 1rem', textAlign: 'left', fontWeight: 600 }}>Criterion</th>
                  <th style={{ padding: '0.85rem 1rem', textAlign: 'left', fontWeight: 600, color: '#a8c8ff' }}>Xentrios</th>
                  <th style={{ padding: '0.85rem 1rem', textAlign: 'left', fontWeight: 600 }}>Dentrix Enterprise</th>
                  <th style={{ padding: '0.85rem 1rem', textAlign: 'left', fontWeight: 600 }}>Denticon</th>
                  <th style={{ padding: '0.85rem 1rem', textAlign: 'left', fontWeight: 600 }}>CareStack</th>
                </tr>
              </thead>
              <tbody>
                {tableRowItems.map((row, i) => (
                  <tr key={row.criterion} style={{ background: i % 2 === 0 ? '#f8f9fc' : '#fff' }}>
                    <td style={{ padding: '0.8rem 1rem', fontWeight: 600, color: 'var(--xen-navy)', verticalAlign: 'top', whiteSpace: 'nowrap' }}>
                      {row.criterion}
                    </td>
                    <td style={{ padding: '0.8rem 1rem', color: '#333', verticalAlign: 'top', background: i % 2 === 0 ? '#eef1ff' : '#f5f6ff' }}>
                      <span style={{ marginRight: '0.35rem', color: row.xentrios.check ? '#00897b' : '#e53935', fontWeight: 700 }}>
                        {row.xentrios.check ? '✓' : '✗'}
                      </span>
                      {row.xentrios.value}
                    </td>
                    <td style={{ padding: '0.8rem 1rem', color: '#333', verticalAlign: 'top' }}>
                      <span style={{ marginRight: '0.35rem', color: row.dentrix.check ? '#00897b' : '#e53935', fontWeight: 700 }}>
                        {row.dentrix.check ? '✓' : '✗'}
                      </span>
                      {row.dentrix.value}
                    </td>
                    <td style={{ padding: '0.8rem 1rem', color: '#333', verticalAlign: 'top' }}>
                      <span style={{ marginRight: '0.35rem', color: row.denticon.check ? '#00897b' : '#e53935', fontWeight: 700 }}>
                        {row.denticon.check ? '✓' : '✗'}
                      </span>
                      {row.denticon.value}
                    </td>
                    <td style={{ padding: '0.8rem 1rem', color: '#333', verticalAlign: 'top' }}>
                      <span style={{ marginRight: '0.35rem', color: row.carestack.check ? '#00897b' : '#e53935', fontWeight: 700 }}>
                        {row.carestack.check ? '✓' : '✗'}
                      </span>
                      {row.carestack.value}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p style={{ marginTop: '1rem', fontSize: '0.8rem', color: '#888' }}>
            This comparison reflects publicly available product information and general market positioning.
            Individual feature sets vary by version and configuration. We recommend validating specific
            requirements with each vendor during evaluation.
          </p>
        </div>
      </section>

      {/* Verdict */}
      <section className="xen-section xen-section-light">
        <div className="xen-container">
          <p className="xen-section-eyebrow">Verdict</p>
          <h2>Which PMS platform fits your DSO's situation?</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.5rem', marginTop: '2rem' }}>
            <div style={{ background: '#fff', border: '2px solid #e0e4f0', borderRadius: '12px', padding: '1.75rem' }}>
              <h3 style={{ color: 'var(--xen-navy)', marginBottom: '0.75rem', fontSize: '1rem' }}>Stay with Dentrix Enterprise if…</h3>
              <ul style={{ paddingLeft: '1.15rem', lineHeight: 1.9, color: '#444', fontSize: '0.92rem' }}>
                <li>Your staff is deeply trained on Dentrix and workflows are well-optimized</li>
                <li>Henry Schein supply and product integrations are core to your operations</li>
                <li>Clinical stability and a large support network outweigh modernization priorities</li>
                <li>You are adding RevTech analytics via NDS DataVault without a PMS change</li>
              </ul>
            </div>
            <div style={{ background: '#fff', border: '2px solid #e0e4f0', borderRadius: '12px', padding: '1.75rem' }}>
              <h3 style={{ color: 'var(--xen-navy)', marginBottom: '0.75rem', fontSize: '1rem' }}>Stay with Denticon if…</h3>
              <ul style={{ paddingLeft: '1.15rem', lineHeight: 1.9, color: '#444', fontSize: '0.92rem' }}>
                <li>Your DSO has well-established Denticon workflows and trained billing staff</li>
                <li>Planet DDS's hosted model meets your infrastructure preferences</li>
                <li>Multi-location centralized billing in Denticon is working well for your group</li>
                <li>You want to add RevTech analytics without disrupting the PMS layer</li>
              </ul>
            </div>
            <div style={{ background: '#fff', border: '2px solid #e0e4f0', borderRadius: '12px', padding: '1.75rem' }}>
              <h3 style={{ color: 'var(--xen-navy)', marginBottom: '0.75rem', fontSize: '1rem' }}>Choose CareStack if…</h3>
              <ul style={{ paddingLeft: '1.15rem', lineHeight: 1.9, color: '#444', fontSize: '0.92rem' }}>
                <li>You want cloud-native architecture without adopting the full Xentrios/NDS/RevTech stack</li>
                <li>An active partner ecosystem and open API are important for your technology strategy</li>
                <li>You are building out a modern PMS foundation and plan to connect RevTech via NDS DataVault</li>
                <li>CareStack's current AI roadmap and feature pace matches your group's timeline</li>
              </ul>
            </div>
            <div style={{ background: '#f0f4ff', border: '2px solid var(--xen-accent)', borderRadius: '12px', padding: '1.75rem' }}>
              <h3 style={{ color: 'var(--xen-navy)', marginBottom: '0.75rem', fontSize: '1rem' }}>Choose Xentrios if…</h3>
              <ul style={{ paddingLeft: '1.15rem', lineHeight: 1.9, color: '#444', fontSize: '0.92rem' }}>
                <li>You want the RCM analytics stack (RevTech) natively integrated — not connected via a separate pipeline</li>
                <li>You are building a new DSO or acquiring practices where no legacy PMS lock-in exists</li>
                <li>AI-assisted workflows embedded in the PMS (not bolt-on) are a strategic priority</li>
                <li>A single managed vendor relationship (NDS + RevTech + Xentrios) reduces complexity at scale</li>
                <li>You are willing to invest in migration for a tighter long-term architecture</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="xen-section" style={{ background: '#fff' }}>
        <div className="xen-container">
          <p className="xen-section-eyebrow">FAQ</p>
          <h2>Common questions about PMS migration and platform selection</h2>
          <div style={{ marginTop: '2rem', display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            {jsonLd['@graph'][1].mainEntity.map((faq) => (
              <div key={faq.name} style={{ background: '#f8f9fc', borderRadius: '10px', padding: '1.5rem 2rem' }}>
                <h3 style={{ color: 'var(--xen-navy)', marginBottom: '0.5rem', fontSize: '1.05rem' }}>{faq.name}</h3>
                <p style={{ color: '#444', lineHeight: 1.75 }}>{faq.acceptedAnswer.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Note about non-disruptive path */}
      <section className="xen-section xen-section-light">
        <div className="xen-container xen-section-center">
          <p className="xen-section-eyebrow">Not ready to migrate?</p>
          <h2>Start with RevTech and NDS — without changing your PMS.</h2>
          <p style={{ maxWidth: '680px', margin: '1rem auto 2rem', color: '#555', lineHeight: 1.75, fontSize: '1rem' }}>
            The NDS DataVault + RevTech analytics stack works with Dentrix Enterprise, Denticon, CareStack, and
            other major PMS platforms today. You can begin capturing denial intelligence, payer analytics, and
            AR visibility immediately without any PMS migration. Xentrios is there if and when a more integrated
            architecture becomes the right move for your group.
          </p>
          <Link to="/demo" className="xen-btn xen-btn-primary" style={{ padding: '0.875rem 2.5rem', fontSize: '1.05rem' }}>
            Talk to a Solutions Engineer
          </Link>
        </div>
      </section>

      {/* CTA */}
      <section className="xen-section xen-section-dark xen-section-center">
        <div className="xen-container">
          <h2>Ready to see Xentrios and the full NDS + RevTech stack?</h2>
          <p className="xen-section-subtitle" style={{ marginTop: '1rem', marginBottom: '2rem' }}>
            Talk to a solutions engineer about your current PMS, your group size, and your analytics goals.
            We'll give you an honest assessment of whether Xentrios is the right architecture for your DSO
            — and what the path forward looks like.
          </p>
          <Link to="/demo" className="xen-btn xen-btn-primary" style={{ padding: '0.875rem 2.5rem', fontSize: '1.05rem' }}>
            Request a Demo
          </Link>
        </div>
      </section>
    </div>
  );
}
